/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SanityImageType } from "./types";
import { useSanityImage } from "./useSanityImage";
import clsx from "clsx";

export interface SanityImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement>,
    SanityImageType {
  options?: {
    __experimentalAspectRatio: boolean;
  };
  config?: Record<string, unknown>;
  jsLazy?: boolean;
  forceRenderPreview?: boolean;
  fit?: "crop" | "original";
}

const SanityImage: React.FC<React.PropsWithChildren<SanityImageProps>> = (
  props
) => {
  const {
    jsLazy = false,
    forceRenderPreview = false,
    fit = "crop",
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hotspot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    crop,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    config,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    asset,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    __typename,
    ...imageProps
  } = props;

  const sanityFit = fit == "crop" ? "max" : "clip";

  const { imageStyle, width, height, srcSet, src, format } =
    useSanityImage({ ...props, fit: sanityFit }) || {};

  // Short circuit for SVG images
  if (format === "svg") {
    return <img src={src} {...props} />;
  }
  const preview = props.asset.metadata?.preview;

  if (forceRenderPreview) {
    if (preview)
      return (
        <PreviewImage
          preview={preview}
          width={width}
          height={height}
          {...imageProps}
        />
      );
    else return null;
  }

  if (jsLazy) {
    return (
      <img
        src={preview}
        data-src={src}
        data-src-set={srcSet}
        width={width}
        height={height}
        style={imageStyle}
        className={clsx("js-lazy-load", props.className)}
        {...imageProps}
      />
    );
  }

  const Image = preview ? ImageWithPreview : "img";

  return (
    <Image
      preview={preview}
      style={imageStyle}
      width={width}
      height={height}
      src={src}
      srcSet={srcSet}
      {...imageProps}
    />
  );
};

export default SanityImage;

const PreviewImage = ({ preview, ...props }) => {
  return (
    <img
      src={preview}
      alt={props.alt}
      id={props.id}
      className={props.className}
      style={props.style}
      width={props.width}
      height={props.height}
      data-lqip
    />
  );
};

const ImageWithPreview = ({ preview, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement | null>(null);

  const onLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (ref.current && ref.current.complete) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!loaded && <PreviewImage preview={preview} {...props} />}
      <img
        ref={ref}
        onLoad={onLoad}
        data-loading={loaded ? null : true}
        {...props}
      />
    </>
  );
};
